import { useReducer, Reducer } from "react";
import { v4 as uuidv4 } from "uuid";
import {
	GroupItem,
	GroupState,
	IExcel,
} from "../common/batchNotificationInterface";
import { assertUnreachable } from "../common/shared";
import {
	GroupDetail,
	PhoneNumberDynamicVariablesDetail,
	DynamicVariableDetail,
} from "../models/BatchMessage/BatchMessageGroupDetailModel";

//define actions we can preform
type Action =
	| { type: "ADD_NEW_GROUP_ITEM" }
	| { type: "REMOVE_GROUP_ITEM"; payload: { id: string } }
	| { type: "SET_GROUP_ITEM_TITLE"; payload: { id: string; name: string } }
	| {
			type: "SET_GROUP_ITEM_EXCEL_DATA";
			payload: {
				id: string;
				excelData: IExcel;
			};
	  }
	| {
			type: "INITIALIZE_BATCH_GROUP_DETAILS";
			payload: { groupDetailList: GroupDetail[]; excelData: IExcel[] };
	  };

export function GetExcelDataColumn(excelDataValue: IExcel) {
	const excelDataDetail: any = excelDataValue.contentData;
	const tempObject: any = {};
	const tempList = Object.values(excelDataValue.header);
	tempList.forEach((element: any, index: number) => {
		tempObject[element] = element;
	});
	if (JSON.stringify(excelDataDetail[0]) === JSON.stringify(tempObject)) {
		excelDataDetail.shift();
	} else {
		excelDataDetail.unshift(tempObject);
	}
	return excelDataDetail;
}

export function GetHeaderValue(
	isHeaderIncluded: boolean = false,
	staticHeader: string[] = [],
	excelData: IExcel
): string[] | undefined {
	if (isHeaderIncluded) {
		return excelData.header;
	} else {
		return staticHeader && staticHeader.length > 0
			? staticHeader
			: excelData.staticHeader;
	}
}

function getPhoneNumberListFromExcelData(
	groupDetail: GroupDetail,
	excelData: IExcel
) {
	const groupDetailTemp = { ...groupDetail };
	const phoneNumberList: PhoneNumberDynamicVariablesDetail[] = [];
	if (excelData.contentData && excelData.contentData.length > 0) {
		const headerList = GetHeaderValue(
			excelData.isHeaderIncluded,
			excelData.staticHeader,
			excelData
		);
		const indexId =
			headerList !== undefined
				? headerList.findIndex((x: any) => x === excelData.selectedColumn)
				: 0;
		excelData.contentData.forEach((element: any) => {
			const variables: DynamicVariableDetail[] = [];
			Object.entries(excelData.variableMapping).forEach((variable) =>
				variables.push({
					key: variable[0],
					value: String(element[variable[1]]),
				})
			);
			phoneNumberList.push({
				phoneNumber: Object.values(element)[indexId] as string,
				variables,
			});
		});
		groupDetailTemp.phoneNumberCount = phoneNumberList.length;
		groupDetailTemp.phoneNumbers = phoneNumberList;
		groupDetailTemp.name = groupDetailTemp.name.trim();
	}
	return groupDetailTemp;
}
export const useBatchGroupConfiguration = () => {
	return useReducer<Reducer<GroupState, Action>, undefined>(
		(state, action): GroupState => {
			//reducer needs to return new state based on previous state and action parsed in
			switch (action.type) {
				case "ADD_NEW_GROUP_ITEM":
					const id = uuidv4();
					return {
						...state,
						items: [
							...state.items,
							{
								rowId: id,
								groupDetail: new GroupDetail(),
							},
						],
						excelData: [
							...state.excelData,
							{
								id: id,
								header: [],
								contentData: [],
								variableMapping: {},
								isHeaderIncluded: false,
								selectedColumn: "Column 1",
							},
						],
					};
				case "REMOVE_GROUP_ITEM":
					const updatedItems = [...state.items];
					const updateExcelData = [...state.excelData];
					const itemIndexToRemove = updatedItems.findIndex(
						(e) => e.rowId === action.payload.id
					);
					if (itemIndexToRemove === -1) {
						return state;
					}
					updatedItems.splice(itemIndexToRemove, 1);
					const itemIndexToRemoveOfExcel = updateExcelData.findIndex(
						(e) => e.id === action.payload.id
					);
					if (itemIndexToRemoveOfExcel === -1) {
						return state;
					}
					updateExcelData.splice(itemIndexToRemoveOfExcel, 1);
					return {
						...state,
						items: updatedItems,
						excelData: updateExcelData,
					};
				case "SET_GROUP_ITEM_TITLE":
					const updatedItemsT = [...state.items];
					const itemIndexToUpdateTitle = updatedItemsT.findIndex(
						(e) => e.rowId === action.payload.id
					);
					if (itemIndexToUpdateTitle === -1) {
						return state;
					}
					updatedItemsT.splice(itemIndexToUpdateTitle, 1, {
						...updatedItemsT[itemIndexToUpdateTitle],
						groupDetail: {
							...updatedItemsT[itemIndexToUpdateTitle].groupDetail,
							name: action.payload.name,
						},
					});
					return {
						...state,
						items: updatedItemsT,
					};
				case "SET_GROUP_ITEM_EXCEL_DATA":
					const updateExcelDataTemp = [...state.excelData];
					const itemIndexToUpdateExcelData = updateExcelDataTemp.findIndex(
						(e) => e.id === action.payload.id
					);
					if (itemIndexToUpdateExcelData === -1) {
						return state;
					}
					updateExcelDataTemp.splice(itemIndexToUpdateExcelData, 1, {
						...updateExcelDataTemp[itemIndexToUpdateExcelData],
						...action.payload.excelData,
					});
					const updateGroupDetailTemp = [...state.items];
					const itemIndexToUpdateGroupDetail = updateExcelDataTemp.findIndex(
						(e) => e.id === action.payload.id
					);
					if (itemIndexToUpdateGroupDetail === -1) {
						return state;
					}
					updateGroupDetailTemp.splice(itemIndexToUpdateExcelData, 1, {
						...updateGroupDetailTemp[itemIndexToUpdateExcelData],
						groupDetail: {
							...getPhoneNumberListFromExcelData(
								updateGroupDetailTemp[itemIndexToUpdateExcelData].groupDetail,
								action.payload.excelData
							),
						},
					});
					return {
						...state,
						excelData: updateExcelDataTemp,
						items: updateGroupDetailTemp,
					};
				case "INITIALIZE_BATCH_GROUP_DETAILS":
					const items: GroupItem[] = [];
					const excelData: IExcel[] = [];
					const tempIdList: string[] = [];
					for (
						let index = 0;
						index < action.payload.groupDetailList.length;
						index++
					) {
						tempIdList.push(uuidv4());
					}
					action.payload.groupDetailList.forEach((group, index: number) => {
						items.push({
							rowId: tempIdList[index],
							groupDetail: group,
						});
					});
					action.payload.excelData.forEach((excelDataItem, index: number) => {
						excelData.push({
							...excelDataItem,
							id: tempIdList[index],
						});
					});
					return {
						items: items,
						excelData: excelData,
					};
			}
			assertUnreachable(action);
		},
		undefined,
		() => ({ items: [], excelData: [] })
	);
};
